import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import WelcomeHeader from './components/WelcomeHeader';
import StatsCard from './components/StatsCard';
import BlockHoursCard from './components/BlockHoursCard';
import PayStatusCard from './components/PayStatusCard';
import RecentRotationsTable from './components/RecentRotationsTable';
import QuickActions from './components/QuickActions';
import BlockHoursChart from './components/BlockHoursChart';
import CreditHoursChart from './components/CreditHoursChart';
import { getRotations } from '../../services/apiService'; // ✅ Using API instead of direct Supabase
import { useAuth } from '../../contexts/AuthContext';
import { getCurrentBidPeriod, getBidPeriodMonthRefs, getCurrentBidPeriodName } from '../../utils/bidPeriodHelper';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userSettings, employeeId, employeeName, position, loading: authLoading, needsOnboarding } = useAuth(); // ✅ Get from auth context
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [blockHours, setBlockHours] = useState({});
  const [recentRotations, setRecentRotations] = useState([]);
  const [error, setError] = useState(null);

  const breadcrumbItems = [
    { label: 'Dashboard', icon: 'Gauge' }
  ];

  // ✅ Load cached data immediately on mount
  useEffect(() => {
    const cached = sessionStorage.getItem('dashboard_data');
    if (cached) {
      try {
        const data = JSON.parse(cached);
        setStats(data.stats);
        setBlockHours(data.blockHours);
        setRecentRotations(data.recentRotations);
        console.log('✅ Loaded dashboard from cache');
      } catch (e) {
        console.error('Failed to load cached dashboard data:', e);
      }
    }
  }, []);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [authLoading, user, navigate]);

  // Redirect to onboarding if profile incomplete
  useEffect(() => {
    if (!authLoading && user && needsOnboarding) {
      navigate('/onboarding');
    }
  }, [authLoading, user, needsOnboarding, navigate]);

  useEffect(() => {
    console.log('Dashboard effect triggered:', { user: !!user, needsOnboarding, authLoading, pathname: location.pathname });
    if (!authLoading && user && !needsOnboarding) {
      loadDashboardData();
    }
  }, [user, needsOnboarding, authLoading, location.pathname]);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // ✅ PROPER API FLOW: Use backend API with automatic employee_id injection
      // API service will automatically add employee_id from localStorage (set by auth context)
      const rotationsData = await getRotations({ limit: 100 });
      
      const rotations = rotationsData?.rotations || [];
      
      // Get current PWA bid period
      const currentPeriod = getCurrentBidPeriod();
      const currentBidPeriodDisplay = getCurrentBidPeriodName();
      const bidPeriodMonthRefs = getBidPeriodMonthRefs();
      
      // Calculate dates for rolling block hour periods
      const dayMs = 24 * 60 * 60 * 1000;
      const periods = [30, 60, 90, 180, 360];
      const cutoffDates = {};
      periods.forEach(days => {
        cutoffDates[days] = new Date(Date.now() - days * dayMs).toISOString().split('T')[0];
      });
      
      // Filter for current bid period
      const bidPeriodRotations = rotations.filter(r => {
        if (!r.month_ref) return false;
        const monthRef = r.month_ref.toLowerCase();
        return bidPeriodMonthRefs.some(ref => monthRef.includes(ref.toLowerCase()));
      });
      
      const totalRotations = rotations.length;
      
      // Calculate rolling block hours
      // Note: API doesn't return parsed_json, so we use dates from the response
      const rollingBlockHours = {};
      periods.forEach(days => {
        const cutoff = cutoffDates[days];
        const filtered = rotations.filter(r => {
          const rotDate = r.rotation_start_date || r.created_at?.split('T')[0];
          return rotDate >= cutoff;
        });
        // TODO: Backend should calculate this - for now approximate from credit hours
        rollingBlockHours[days] = filtered.reduce((sum, r) => sum + (r.total_credit_hours || 0), 0);
      });
      
      // Current bid period credit hours
      const bidPeriodCredits = bidPeriodRotations.reduce((sum, r) => sum + (r.total_credit_hours || 0), 0);

      // ✅ Set stats from API data
      const statsData = {
        totalRotations: totalRotations,
        bidPeriodCredits: bidPeriodCredits,
        currentBidPeriod: currentBidPeriodDisplay,
        totalPay: 0,
        payDiscrepancy: 0,
        payStatus: 'pending'
      };
      setStats(statsData);

      // Set rolling block hours
      setBlockHours(rollingBlockHours);

      // Transform rotations data for display (take first 5)
      const recentRotationsData = rotations.slice(0, 5).map(r => ({
        id: r.id,
        rotationNumber: r.rotation_number,
        startDate: r.rotation_start_date,
        endDate: r.rotation_end_date,
        creditHours: r.total_credit_hours || 0,
        blockHours: r.total_block_hours || 0,  // Add block hours
        status: r.is_valid ? 'pending' : 'invalid',
        type: r.assignment_type || 'Line Holder'
      }));
      
      setRecentRotations(recentRotationsData);
      
      // ✅ Cache dashboard data for quick reload
      sessionStorage.setItem('dashboard_data', JSON.stringify({
        stats: statsData,
        blockHours: rollingBlockHours,
        recentRotations: recentRotationsData
      }));
      console.log('✅ Dashboard data loaded and cached');
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
      // Set empty defaults on error
      setStats({ totalRotations: 0, bidPeriodCredits: 0, currentBidPeriod: 'N/A', totalPay: 0, payDiscrepancy: 0, payStatus: 'pending' });
      setBlockHours({ 30: 0, 60: 0, 90: 0, 180: 0, 360: 0 });
      setRecentRotations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadSuccess = (result) => {
    console.log('Upload successful:', result);
    // Reload dashboard data after successful upload
    loadDashboardData();
  };

  const handleViewPayComparison = () => {
    navigate('/pay-comparison');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar />
        <div className="lg:ml-60 min-h-screen">
          <div className="p-4 md:p-6 lg:p-8 pb-20 lg:pb-8">
            <div className="space-y-6">
              <div className="skeleton h-32 w-full" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                <div className="skeleton h-48" />
                <div className="skeleton h-48" />
                <div className="skeleton h-48 md:col-span-2" />
              </div>
              <div className="skeleton h-96 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <div className="lg:ml-60 min-h-screen">
        <div className="p-4 md:p-6 lg:p-8 pb-20 lg:pb-8">
          {/* Breadcrumb Navigation */}
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {/* Welcome Header */}
          <div className="mb-6 md:mb-8">
            <WelcomeHeader 
              employeeName={employeeName || 'Pilot'} 
              employeeId={employeeId || 'Not Set'}
            />
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            <StatsCard
              title="Total Rotations"
              value={stats?.totalRotations}
              icon="Plane"
              color="cyan"
            />
            <StatsCard
              title="Credit Hours"
              value={stats?.bidPeriodCredits}
              unit="hrs"
              icon="Clock"
              color="blue"
              subtitle={stats?.currentBidPeriod || 'Current Bid'}
            />
            <BlockHoursCard blockHours={blockHours} />
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 md:mb-8">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">Block Hours Trend</h3>
              <BlockHoursChart rotations={recentRotations} />
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">Credit Hours by Month</h3>
              <CreditHoursChart rotations={recentRotations} />
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mb-6 md:mb-8">
            <QuickActions
              onUploadSuccess={handleUploadSuccess}
              onViewPayComparison={handleViewPayComparison}
            />
          </div>

          {/* Recent Rotations Table */}
          <RecentRotationsTable rotations={recentRotations} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;